'use es6';

import PropTypes from 'prop-types';
import UIForm from 'UIComponents/form/UIForm';
import UIFormControl from 'UIComponents/form/UIFormControl';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import Toggle from '../Toggle';
import MarketingContactsToggle from './MarketingContactsToggle';
import FollowupEmail from 'ContentComponents/cmv2/form/formAutomation/followupEmail/FollowupEmail';
import FormWorkflowDependencies from 'ContentComponents/cmv2/form/formAutomation/connectedWorkflows/FormWorkflowDependencies';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = key => `ContentComponents.cmv2.fields.form.${key}`;
const disabledTooltipTitle = /*#__PURE__*/_jsx(FormattedMessage, {
  message: i18nKey('withInlineEditing.automation.marketingContacts.noPermissions')
});
const NewContactToggle = ({
  value,
  onChange,
  hasFormsWriteAccess,
  readOnly
}) => /*#__PURE__*/_jsx(Toggle, {
  id: "always-create-contact-toggle",
  label: /*#__PURE__*/_jsx(FormattedMessage, {
    message: i18nKey('withInlineEditing.automation.newContacts')
  }),
  value: value,
  onChange: () => onChange(!value),
  disabledTooltipTitle: !hasFormsWriteAccess && disabledTooltipTitle,
  disabled: !hasFormsWriteAccess,
  readOnly: readOnly
});
const FormAutomation = ({
  useOnDark,
  formGuid,
  formName = '',
  alwaysCreateContactForNewEmail = false,
  shouldShowMarketingContactsToggle = false,
  hasMarketingContactsPermissions = false,
  isCreatingMarketingContacts = false,
  followupEmailId,
  legacyFollowupEmailId,
  clearLegacyFollowupEmail,
  hasFormsWriteAccess = true,
  onAlwaysCreateContactForNewEmailChange = () => {},
  onIsCreatingMarketingContactsChange = () => {},
  hasWorkflowsAccess,
  contentId,
  hasAutomatedEmailEditorAccess,
  hasEmailMarketingAccess,
  hasInlineEmailEditorAccess,
  hasWorkflowsAppAccess,
  hasWorkflowsDeleteAccess,
  hasWorkflowsWriteAccess,
  isFreePortal,
  onAddWorkflowEvent,
  onEditWorkflowEvent,
  onRemoveWorkflowEvent,
  onChangeFollowupEmailEvent,
  onFollowupEmailIdChange,
  readOnly,
  followupEmailSelectedForForm,
  scrollToFollowupEmail,
  webinarSelect
}) => /*#__PURE__*/_jsxs(UIForm, {
  children: [/*#__PURE__*/_jsx(UIFormControl, {
    children: /*#__PURE__*/_jsx(NewContactToggle, {
      value: alwaysCreateContactForNewEmail,
      onChange: onAlwaysCreateContactForNewEmailChange,
      hasFormsWriteAccess: hasFormsWriteAccess,
      readOnly: readOnly
    })
  }), shouldShowMarketingContactsToggle && /*#__PURE__*/_jsx(UIFormControl, {
    children: /*#__PURE__*/_jsx(MarketingContactsToggle, {
      disabledTooltipTitle: disabledTooltipTitle,
      formName: formName,
      value: isCreatingMarketingContacts,
      hasPermissions: hasMarketingContactsPermissions,
      hasFormsWriteAccess: hasFormsWriteAccess,
      onChange: onIsCreatingMarketingContactsChange,
      readOnly: readOnly
    })
  }), hasFormsWriteAccess && webinarSelect && /*#__PURE__*/_jsx(UIFormControl, {
    children: webinarSelect
  }), hasEmailMarketingAccess && contentId && /*#__PURE__*/_jsx(FollowupEmail, {
    clearLegacyFollowupEmail: clearLegacyFollowupEmail,
    contentId: contentId,
    followupEmailId: followupEmailId,
    formGuid: formGuid,
    hasAutomatedEmailEditorAccess: hasAutomatedEmailEditorAccess,
    hasInlineEmailEditorAccess: hasInlineEmailEditorAccess,
    hasWorkflowsAppAccess: hasWorkflowsAppAccess,
    followupEmailSelectedForForm: followupEmailSelectedForForm,
    legacyFollowupEmailId: legacyFollowupEmailId,
    onChangeFollowupEmailEvent: onChangeFollowupEmailEvent,
    onFollowupEmailIdChange: onFollowupEmailIdChange,
    readOnly: readOnly,
    scrollToFollowupEmail: scrollToFollowupEmail
  }), hasWorkflowsAccess && /*#__PURE__*/_jsx(FormWorkflowDependencies, {
    contentId: contentId,
    formGuid: formGuid,
    hasWorkflowsAppAccess: hasWorkflowsAppAccess,
    hasWorkflowsDeleteAccess: hasWorkflowsDeleteAccess,
    hasWorkflowsWriteAccess: hasWorkflowsWriteAccess,
    isFreePortal: isFreePortal,
    onAddWorkflowEvent: onAddWorkflowEvent,
    onEditWorkflowEvent: onEditWorkflowEvent,
    onRemoveWorkflowEvent: onRemoveWorkflowEvent,
    readOnly: readOnly,
    useOnDark: useOnDark
  }), /*#__PURE__*/_jsx(FloatingAlertList, {})]
});
FormAutomation.propTypes = {
  contentId: PropTypes.number,
  followupEmailId: PropTypes.number,
  followupEmailSelectedForForm: PropTypes.object,
  formGuid: PropTypes.string.isRequired,
  formName: PropTypes.string.isRequired,
  useOnDark: PropTypes.bool,
  alwaysCreateContactForNewEmail: PropTypes.bool,
  isCreatingMarketingContacts: PropTypes.bool,
  shouldShowMarketingContactsToggle: PropTypes.bool,
  hasMarketingContactsPermissions: PropTypes.bool,
  legacyFollowupEmailId: PropTypes.number,
  clearLegacyFollowupEmail: PropTypes.func,
  onAlwaysCreateContactForNewEmailChange: PropTypes.func,
  onIsCreatingMarketingContactsChange: PropTypes.func,
  onFollowupEmailIdChange: PropTypes.func,
  hasAutomatedEmailEditorAccess: PropTypes.bool,
  hasEmailMarketingAccess: PropTypes.bool,
  hasInlineEmailEditorAccess: PropTypes.bool,
  hasFormsWriteAccess: PropTypes.bool,
  hasWorkflowsAccess: PropTypes.bool,
  hasWorkflowsAppAccess: PropTypes.bool,
  hasWorkflowsDeleteAccess: PropTypes.bool,
  hasWorkflowsWriteAccess: PropTypes.bool,
  isFreePortal: PropTypes.bool,
  onAddWorkflowEvent: PropTypes.func,
  onEditWorkflowEvent: PropTypes.func,
  onRemoveWorkflowEvent: PropTypes.func,
  onChangeFollowupEmailEvent: PropTypes.func,
  readOnly: PropTypes.bool,
  scrollToFollowupEmail: PropTypes.func.isRequired,
  webinarSelect: PropTypes.node
};
export default FormAutomation;